import { mdiChevronDown, mdiChevronUp, mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useState } from "react";
import { currencyFormat } from "../../../../utils/currency_format";
import { useOrderDetailCtx } from "../../../../context_providers/order_detail_context";

interface BottomSheetWidgetProps {}

const BottomSheetViewWidget: FunctionComponent<BottomSheetWidgetProps> = () => {
  const { order, useBal, balTxnAmt } = useOrderDetailCtx();
  const [expanded, setExpanded] = useState(false);

  const currency = order.currency || {
    short_name: "INR",
    symbol: "₹",
    formal_name: "Indian Rupee",
  };
  const isNotINR = currency.short_name !== "INR";
  const exchangeRate = order.currency_to_inr || 1;

  const productsTotal = (order.products ?? []).reduce(
    (sum, product) => sum + (product.qty ?? 0) * (product.price ?? 0),
    0,
  );
  const serviceTotal = (order.services ?? []).reduce(
    (sum, service) => sum + (service.qty ?? 0) * (service.price ?? 0),
    0,
  );
  const rawMaterialsTotal = (order.raw_materials ?? []).reduce(
    (sum, raw_material) =>
      sum + (raw_material.qty ?? 0) * (raw_material.price ?? 0),
    0,
  );

  const total = order.subtotal + order.excess - order.discount;
  const balance = total - (order.paid + (useBal ? balTxnAmt : 0));

  const toINR = (val: number) => val * exchangeRate;

  return (
    <div>
      {expanded && (
        <>
          <div className="flex justify-between items-center mb-2">
            <p className="font-semibold italic">Order Summary</p>
            <div
              onClick={() => setExpanded(false)}
              className="hover:scale-110 duration-200 transform bg-gray-100 p-1 rounded-full hover:text-myPrimaryColor cursor-pointer"
            >
              <Icon path={mdiClose} className="h-4 w-4" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-8 gap-1 text-sm">
            <div className="col-span-4">
              <div className="flex justify-between w-full">
                <div className="font-semibold">Products</div>
                <div>
                  {isNotINR ? (
                    <>
                      {currencyFormat(productsTotal, currency.short_name)}{" "}
                      <span className="text-gray-500 text-xs ml-2">
                        ({currencyFormat(toINR(productsTotal), "INR")})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(productsTotal, "INR")}</>
                  )}
                </div>
              </div>

              <div className="flex justify-between w-full">
                <div className="font-semibold">Services</div>
                <div>
                  {isNotINR ? (
                    <>
                      {currencyFormat(serviceTotal, currency.short_name)}{" "}
                      <span className="text-gray-500 text-xs ml-2">
                        ({currencyFormat(toINR(serviceTotal), "INR")})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(serviceTotal, "INR")}</>
                  )}
                </div>
              </div>

              <div className="flex justify-between w-full">
                <div className="font-semibold">Raw Materials</div>
                <div>
                  {isNotINR ? (
                    <>
                      {currencyFormat(rawMaterialsTotal, currency.short_name)}{" "}
                      <span className="text-gray-500 text-xs ml-2">
                        ({currencyFormat(toINR(rawMaterialsTotal), "INR")})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(rawMaterialsTotal, "INR")}</>
                  )}
                </div>
              </div>

              <div className="flex justify-between w-full">
                <div className="font-semibold">Subtotal</div>
                <div>
                  {isNotINR ? (
                    <>
                      {currencyFormat(order.subtotal, currency.short_name)}{" "}
                      <span className="text-gray-500 text-xs ml-2">
                        ({currencyFormat(toINR(order.subtotal), "INR")})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(order.subtotal, "INR")}</>
                  )}
                </div>
              </div>

              <div className="flex justify-between w-full my-1">
                <div className="font-semibold">Total</div>
                <div className="font-semibold">
                  {isNotINR ? (
                    <>
                      {currencyFormat(total, currency.short_name)}{" "}
                      <span className="text-gray-500 text-xs ml-2">
                        ({currencyFormat(toINR(total), "INR")})
                      </span>
                    </>
                  ) : (
                    <>{currencyFormat(total, "INR")}</>
                  )}
                </div>
              </div>
            </div>
          </div>
          <hr className="my-2" />
        </>
      )}

      <div className="flex justify-between mt-2">
        <div className="flex gap-2 items-center">
          <p className="font-semibold">
            Total:&nbsp;
            {isNotINR ? (
              <>
                {currencyFormat(balance, currency.short_name)}
                <span className="text-gray-500 text-sm ml-1">
                  ({currencyFormat(toINR(balance), "INR")})
                </span>
              </>
            ) : (
              <>{currencyFormat(balance, "INR")}</>
            )}
          </p>

          <span
            onClick={() => setExpanded((o) => !o)}
            className="hover:bg-gray-100 rounded-full p-1 hover:scale-110 transform duration-200 cursor-pointer"
          >
            <Icon
              path={expanded ? mdiChevronDown : mdiChevronUp}
              className="h-5 w-5"
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default BottomSheetViewWidget;
